import React, { useEffect, useState } from "react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "../../contexts/ContextProvider";
import axiosClient from "../../axios-client";
import { toast } from "react-toastify";
import Loading from "../../components/Loading";
import HourLoading from "../../components/HourLoading";
import MySelect from "../../components/MySelect";
import { Button } from "../../components";
import { IoIosSearch } from "react-icons/io";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const EtatAnnuelEcole = () => {
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { token, setToken, user } = useStateContext();
  const [scolaires, setScolaires] = useState([]);
  const [selectedScolaire, setSelectedScolaire] = useState(null);
  const [inputValueScolaire, setValueScolaire] = useState('');
  const [totalDepense, setTotalDepense] = useState(0);
  const [totalRemboursements, setTotalRemboursements] = useState(0); // Nouvelle variable d'état
  const [req, setReq] = useState({
    scolaire: '',
  });

  useEffect(() => {
    getScolaires();
  }, []);

  const getScolaires = () => {
    axiosClient.get('/getScolaires')
      .then((data) => {
        setScolaires(data.data.scolaires);
      }).catch(err => {
        navigate('/500');
      })
  }

  const getFacturesDash = () => {
    setLoading(true);
    axiosClient.get('/facturesDash/' + req.scolaire)
      .then(response => {
        setRowData(response.data.facturesDash);
        setTotalDepense(response.data.depenses);
        setTotalRemboursements(response.data.remboursements); // Mise à jour des remboursements
        setLoading(false);
        setErrors(null);
      }).catch(err => {
        const response = err.response;
        if (response.status === 401) {
          setToken(null);
        } else {
          if (response.status === 422) {
            setErrors(response.data.errors);
            setLoading(false);
            toast.error('Une erreur est survenue!', {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
            });
          } else {
            if (response.status === 403) {
              navigate('/403');
            } else {
              if (response.status === 404) {
                navigate('/404');
              } else {
                navigate('/500');
              }
            }
          }
        }
      })
  }

  const handleChangeScolaire = value => {
    setSelectedScolaire(value);
    setReq({ ...req, scolaire: value.id });
  }

  const handleInputChangeScolaire = value => {
    setValueScolaire(value);
  };

  // === Données brutes ===
  const [rawData, setRowData] = useState([]);

  const filteredData = rawData.filter(item => item.etat === 1);

  // === Groupement par classe ===
  const groupedData = filteredData.reduce((acc, curr) => {
    if (!acc[curr.classe]) {
      acc[curr.classe] = { paye: 0, nonPaye: 0, dispense: 0 };
    }
    acc[curr.classe].paye += curr.paye;
    acc[curr.classe].nonPaye += curr.nonPaye;
    acc[curr.classe].dispense += curr.dispense;
    return acc;
  }, {});

  // Conversion en tableau et tri croissant
  const classesData = Object.entries(groupedData)
    .map(([classe, data]) => ({
      classe,
      ...data
    }))
    .sort((a, b) => a.classe.localeCompare(b.classe, "fr", { numeric: true }));

  const totalPaye = classesData.reduce((sum, c) => sum + c.paye, 0);
  const totalNonPaye = classesData.reduce((sum, c) => sum + c.nonPaye, 0);
  const totalDispense = classesData.reduce((sum, c) => sum + c.dispense, 0);
  const totalGlobal = totalPaye + totalNonPaye + totalDispense;
  const solde = totalPaye - totalDepense - totalRemboursements; // Mise à jour du calcul du solde

  // === Données pour graphique barres ===
  const barData = {
    labels: classesData.map(c => c.classe),
    datasets: [
      {
        label: "Payé (MRU)",
        data: classesData.map(c => c.paye),
        backgroundColor: "#4CAF50",
        borderColor: "#45a049",
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false
      },
      {
        label: "Non payé (MRU)",
        data: classesData.map(c => c.nonPaye),
        backgroundColor: "#F44336",
        borderColor: "#da190b",
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false
      },
      {
        label: "Dispensé (MRU)",
        data: classesData.map(c => c.dispense),
        backgroundColor: "#FFC107",
        borderColor: "#ff8f00",
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12, family: 'Arial' }
        }
      },
      title: {
        display: true,
        text: "Montants par Classe (MRU)",
        font: { size: 16, family: 'Arial', weight: 'bold' },
        padding: { top: 10, bottom: 20 }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.raw.toLocaleString()} MRU`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0,0,0,0.05)',
          drawBorder: false
        },
        ticks: {
          callback: value => `${value.toLocaleString()} MRU`,
          font: { family: 'Arial' }
        }
      },
      x: {
        grid: { display: false },
        ticks: {
          font: { family: 'Arial' },
          maxRotation: 45
        }
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart'
    }
  };

  // === Données pour Pie Chart ===
  const pieData = {
    labels: ["Payé", "Non payé", "Dispensé"],
    datasets: [
      {
        data: [totalPaye, totalNonPaye, totalDispense],
        backgroundColor: ["#4CAF50", "#F44336", "#FFC107"],
        borderColor: ["#45a049", "#da190b", "#ff8f00"],
        borderWidth: 2,
        hoverOffset: 10
      }
    ]
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12, family: 'Arial' }
        }
      },
      title: {
        display: true,
        text: "Répartition Totale des Factures",
        font: { size: 16, family: 'Arial', weight: 'bold' },
        padding: { top: 10, bottom: 20 }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        callbacks: {
          label: function (context) {
            const montant = context.raw;
            const percent = totalGlobal > 0 ? ((montant / totalGlobal) * 100).toFixed(2) : 0;
            return `${context.label}: ${montant.toLocaleString()} MRU (${percent}%)`;
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1000
    }
  };

  // === Tableau récapitulatif global ===
  const recapData = [
    { label: "Payé", montant: totalPaye, couleur: "#4CAF50" },
    { label: "Non payé", montant: totalNonPaye, couleur: "#F44336" },
    { label: "Dispensé", montant: totalDispense, couleur: "#FFC107" }
  ];

  // Debug: Vérifier si les données sont disponibles
  if (classesData.length === 0) {
    return (
      <div className="p-8 bg-gray-50 min-h-screen" style={{ fontFamily: "Arial, sans-serif" }}>
        <h2 className="text-3xl font-bold text-gray-800 mb-8 flex items-center">
          <span className="text-2xl mr-3">📊</span>
          Etat annuel de l'école
        </h2>
        <div className="flex w-full gap-3 flex-wrap md:flex-nowrap lg:flex-nowrap xl:flex-nowrap justify-between bg-white rounded-2xl shadow-lg border mb-8 border-gray-100 p-6">
          <MySelect
            label="Année scolaire"
            labelAr="السنة الدراسية"
            placeholder="Scolaire..."
            cacheOptions
            defaultOptions
            value={selectedScolaire}
            getOptionLabel={e => e.code}
            getOptionValue={e => e.id}
            options={scolaires}
            onInputChange={handleInputChangeScolaire}
            onChange={handleChangeScolaire}
          />
          <div className='mt-8'>
            <Button
              onClick={() => getFacturesDash()}
              color="bg-yellow-500"
              icon={<IoIosSearch />}
              size="20"
              text="Chercher"
              textColor="text-white"
            />
          </div>
        </div>
        {loading ? (
          <HourLoading />
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <p className="text-gray-600">Aucune donnée disponible pour l'affichage des graphiques.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen" style={{ fontFamily: "Arial, sans-serif" }}>
      <h2 className="text-3xl font-bold text-gray-800 mb-8 flex items-center">
        <span className="text-2xl mr-3">📊</span>
        Etat annuel de l'école
      </h2>

      <div className="flex w-full gap-3 flex-wrap md:flex-nowrap lg:flex-nowrap xl:flex-nowrap justify-between bg-white rounded-2xl shadow-lg border mb-8 border-gray-100 p-6">
        <MySelect
          label="Année scolaire"
          labelAr="السنة الدراسية"
          placeholder="Scolaire..."
          cacheOptions
          defaultOptions
          value={selectedScolaire}
          getOptionLabel={e => e.code}
          getOptionValue={e => e.id}
          options={scolaires}
          onInputChange={handleInputChangeScolaire}
          onChange={handleChangeScolaire}
        />
        <div className='mt-8'>
          <Button
            onClick={() => getFacturesDash()}
            color="bg-yellow-500"
            icon={<IoIosSearch />}
            size="20"
            text="Chercher"
            textColor="text-white"
          />
        </div>
      </div>

      {loading ? (
        <HourLoading />
      ) : (
        <>
          {/* Bar Chart Section */}
          <div className="mb-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Graphique des Factures par Classe</h3>
              <div
                className="relative h-80 w-full flex justify-center items-center bg-gray-50 rounded-lg"
                style={{ minHeight: '320px' }}
              >
                {classesData.length > 0 ? (
                  <Bar
                    data={barData}
                    options={barOptions}
                    fallbackContent={<p className="text-gray-500">Chargement du graphique...</p>}
                  />
                ) : (
                  <p className="text-gray-500">Aucune classe à afficher</p>
                )}
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Pie Chart Column */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6 lg:mb-0">
                <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                  <span className="text-xl mr-2">🥧</span>
                  Répartition Globale
                </h3>
                <div className="relative h-64 w-full flex justify-center items-center bg-gray-50 rounded-lg">
                  {totalGlobal > 0 ? (
                    <Pie
                      data={pieData}
                      options={pieOptions}
                    />
                  ) : (
                    <p className="text-gray-500 text-center">Aucune donnée pour le graphique en secteurs</p>
                  )}
                </div>
              </div>
            </div>

            {/* Global Details Table */}
            <div className="lg:col-span-2">
              <div className="bg-white w-full rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                  <span className="text-xl mr-2">📋</span>
                  Détails Globaux
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                          Statut
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                          Montant (MRU)
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                          %
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {recapData.map((item, index) => {
                        const percentage = totalGlobal > 0 ? ((item.montant / totalGlobal) * 100).toFixed(2) : 0;
                        return (
                          <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                            <td
                              className="px-4 py-4 whitespace-nowrap text-sm font-medium border-b border-gray-200"
                              style={{ color: item.couleur }}
                            >
                              {item.label}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right border-b border-gray-200 font-semibold">
                              {item.montant.toLocaleString()}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right border-b border-gray-200">
                              <div className="flex items-center justify-end">
                                <span className="mr-3 font-semibold">{percentage}%</span>
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="h-2 rounded-full transition-all duration-300"
                                    style={{
                                      width: `${percentage}%`,
                                      backgroundColor: item.couleur
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-gradient-to-r from-blue-50 to-blue-100 font-bold">
                        <td className="px-4 py-4 text-sm text-gray-900 border-t-2 border-blue-200 font-bold">
                          TOTAL
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right border-t-2 border-blue-200 font-bold">
                          {totalGlobal.toLocaleString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right border-t-2 border-blue-200 font-bold">
                          100.00%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-lg font-bold text-gray-800 flex items-center">
                    💰 Total Global: <span className="ml-2 text-blue-600">{totalGlobal.toLocaleString()} MRU</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Class Details Table */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <span className="text-xl mr-2">📚</span>
                Détails par Classe
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Classe
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Payé (MRU)
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Non Payé (MRU)
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Dispensé (MRU)
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Total Classe (MRU)
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        % Payé
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        % Non Payé
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        % Dispensé
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {classesData.map((c, index) => {
                      const totalClasse = c.paye + c.nonPaye + c.dispense;
                      const pourcentPaye = totalClasse > 0 ? ((c.paye / totalClasse) * 100).toFixed(2) : 0;
                      const pourcentNonPaye = totalClasse > 0 ? ((c.nonPaye / totalClasse) * 100).toFixed(2) : 0;
                      const pourcentDispense = totalClasse > 0 ? ((c.dispense / totalClasse) * 100).toFixed(2) : 0;

                      return (
                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-b border-gray-200">
                            {c.classe}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 font-bold text-right border-b border-gray-200">
                            {c.paye.toLocaleString()}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600 font-bold text-right border-b border-gray-200">
                            {c.nonPaye.toLocaleString()}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-yellow-600 font-bold text-right border-b border-gray-200">
                            {c.dispense.toLocaleString()}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold text-right border-b border-gray-200">
                            {totalClasse.toLocaleString()}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right border-b border-gray-200">
                            <div className="flex items-center justify-end">
                              <span className="mr-2">{pourcentPaye}%</span>
                              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-green-500 h-2 rounded-full"
                                  style={{ width: `${pourcentPaye}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right border-b border-gray-200">
                            <div className="flex items-center justify-end">
                              <span className="mr-2">{pourcentNonPaye}%</span>
                              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-red-500 h-2 rounded-full"
                                  style={{ width: `${pourcentNonPaye}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right border-b border-gray-200">
                            <div className="flex items-center justify-end">
                              <span className="mr-2">{pourcentDispense}%</span>
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-yellow-500 h-2 rounded-full"
                                  style={{ width: `${pourcentDispense}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Calcul Solde Section */}
          <div className="lg:col-span-1 mt-2">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <span className="text-xl mr-2">⚖</span>
                Calcul Solde
              </h3>
              <div className="space-y-4">
                <p className="text-lg font-bold text-gray-800 flex items-center">
                  💸 Total Recettes: <span className="ml-2 text-green-600">{totalPaye.toLocaleString()} MRU</span>
                </p>
                <p className="text-lg font-bold text-gray-800 flex items-center">
                  🛠 Total Dépenses: <span className="ml-2 text-blue-600">{totalDepense.toLocaleString()} MRU</span>
                </p>
                <p className="text-lg font-bold text-gray-800 flex items-center">
                  🔄 Total Remboursements: <span className="ml-2 text-red-600">{totalRemboursements.toLocaleString()} MRU</span>
                </p>
                <p className="text-lg font-bold text-gray-800 flex items-center">
                  ⚖ Solde:{" "}
                  <span
                    className="ml-2"
                    style={{ color: solde >= 0 ? "#4CAF50" : "#F44336" }}
                  >
                    {solde.toLocaleString()} MRU
                  </span>
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EtatAnnuelEcole;